import { RepliersListingTransactionType } from './repliers/RepliersListingTransactionType';
import { Units } from './Units';
import { RepliersListingPropertyType } from './repliers/RepliersListingPropertyType';
import { DateTime } from 'luxon';
import { RepliersListingLastStatus } from './repliers/RepliersListingLastStatus';
import { z } from 'zod';
import { RepliersListingStyle } from './repliers/RepliersListingStyle';
import { ListDateFilterLabels } from '../deprecated/ListDateFilterLabels';
import { SoldDateFilterLabels } from '../deprecated/SoldDateFilterLabels';
import { RepliersListingBuildingClass } from './repliers/RepliersListingBuildingClass';
import { RepliersListingBalcony } from './repliers/RepliersListingBalcony';
import { RepliersListingCondoExposure } from './repliers/RepliersListingCondoExposure';
import { multiPolygonGeoJsonFeatureSchema } from './PolygonGeoJsonFeature';
import { PlaceType } from './PlaceType';
import { geographicAreaSchema } from './GeographicArea';

export const BEDROOM_KEYS = ['Any', '0', '1', '2', '3', '4', '5+'] as const;
export type BedroomsKeys = (typeof BEDROOM_KEYS)[number];
export const BATHROOMS_KEYS = ['Any', '0', '1', '2', '3', '4', '5+'] as const;
export type BathroomsKeys = (typeof BATHROOMS_KEYS)[number];

export type DaysOnMarketFilterOption<
  T extends ListDateFilterLabels | SoldDateFilterLabels,
> = {
  label: T;
  getDate: () => null | DateTime;
};

export const maxListedDaysAgoOptions = [
  { label: 'Any', value: null },
  { label: 'Today', value: 0 },
  { label: 'Yesterday', value: 1 },
  { label: '3 Days', value: 3 },
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: '30 Days', value: 30 },
];

export const maxSoldDaysAgoOptions = [
  { label: 'Today', value: 0 },
  { label: 'Yesterday', value: 1 },
  { label: '3 Days', value: 3 },
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '3 Months', value: 3 * 30 },
  { label: '6 Months', value: 6 * 30 },
  { label: '1 Year', value: 365 },
  { label: '2 Years', value: 2 * 365 },
];

export enum BalconyFilterOptionLabel {
  'Open' = 'Open',
  'None' = 'None',
  'Terrace' = 'Terrace',
  'Enclosed' = 'Enclosed',
  'Juliet' = 'Juliet',
}

export const balconyfilterOptionLabelToRepliersBalconyMap: {
  [key in BalconyFilterOptionLabel]: RepliersListingBalcony;
} = {
  [BalconyFilterOptionLabel.Open]: RepliersListingBalcony.Open,
  [BalconyFilterOptionLabel.None]: RepliersListingBalcony.None,
  [BalconyFilterOptionLabel.Terrace]: RepliersListingBalcony.Terrace,
  [BalconyFilterOptionLabel.Enclosed]: RepliersListingBalcony.Enclosed,
  [BalconyFilterOptionLabel.Juliet]: RepliersListingBalcony.Juliet,
};

export enum CondoExposureFilterOptionLabel {
  'North' = 'North',
  'South' = 'South',
  'East' = 'East',
  'West' = 'West',
  'Northeast' = 'Northeast',
  'Northwest' = 'Northwest',
  'Southeast' = 'Southeast',
  'Southwest' = 'Southwest',
  'Eastwest' = 'Eastwest',
  'Northsouth' = 'Northsouth',
}

export const condoExposureFilterOptionLabelToRepliersExposureMap: {
  [key in CondoExposureFilterOptionLabel]: Array<RepliersListingCondoExposure>;
} = {
  [CondoExposureFilterOptionLabel.North]: [RepliersListingCondoExposure.N],
  [CondoExposureFilterOptionLabel.South]: [RepliersListingCondoExposure.S],
  [CondoExposureFilterOptionLabel.East]: [RepliersListingCondoExposure.E],
  [CondoExposureFilterOptionLabel.West]: [RepliersListingCondoExposure.W],
  [CondoExposureFilterOptionLabel.Northeast]: [RepliersListingCondoExposure.NE],
  [CondoExposureFilterOptionLabel.Northwest]: [RepliersListingCondoExposure.NW],
  [CondoExposureFilterOptionLabel.Southeast]: [RepliersListingCondoExposure.SE],
  [CondoExposureFilterOptionLabel.Southwest]: [RepliersListingCondoExposure.SW],
  [CondoExposureFilterOptionLabel.Eastwest]: [RepliersListingCondoExposure.EW],
  [CondoExposureFilterOptionLabel.Northsouth]: [
    RepliersListingCondoExposure.NS,
  ],
};

export enum StyleFilterOptionLabel {
  'Bungalow' = 'Bungalow',
  'TwoStorey' = '2-Storey',
  'ThreeStorey' = '3-Storey',
  'SplitLevel' = 'Split-Level',
  'Loft' = 'Loft',
  'StackedTownhouse' = 'Stacked Townhouse',
  'Cottage' = 'Cottage',
  'Other' = 'Other',
}

export const styleFilterOptionLabelToRepliersStyleMap: {
  [key in StyleFilterOptionLabel]: Array<RepliersListingStyle>;
} = {
  [StyleFilterOptionLabel.Bungalow]: [
    RepliersListingStyle.Bungalow,
    RepliersListingStyle.BungalowRaised,
    RepliersListingStyle.Bungaloft,
    RepliersListingStyle.Storey_1_Rare,
  ],
  [StyleFilterOptionLabel.TwoStorey]: [
    RepliersListingStyle.Storey_1AndAHalf,
    RepliersListingStyle.Storey_2,
    RepliersListingStyle.Storey_2_Rare,
    RepliersListingStyle.Storey_2_Rare2,
    RepliersListingStyle.Storey_2AndAHalf,
    RepliersListingStyle.Storey_2AndAHalf_Rare,
  ],
  [StyleFilterOptionLabel.ThreeStorey]: [
    RepliersListingStyle.Storey_3,
    RepliersListingStyle.Storey_3_Rare,
    RepliersListingStyle.Storey_3_Rare2,
  ],
  [StyleFilterOptionLabel.SplitLevel]: [
    RepliersListingStyle.Backsplit_3,
    RepliersListingStyle.Backsplit_4,
    RepliersListingStyle.Backsplit_5,
    RepliersListingStyle.Backsplit_All,
    RepliersListingStyle.Sidesplit_3,
    RepliersListingStyle.Sidesplit_4,
    RepliersListingStyle.Sidesplit_5,
    RepliersListingStyle.Sidesplit_All,
    RepliersListingStyle.Sidesplit,
    RepliersListingStyle.Backsplit,
    RepliersListingStyle.SplitLevel,
    RepliersListingStyle.MultiLevelSplit,
  ],
  [StyleFilterOptionLabel.Loft]: [
    RepliersListingStyle.Loft,
    RepliersListingStyle.IndustrialLoft,
    RepliersListingStyle.WarehouseLoft,
  ],
  [StyleFilterOptionLabel.StackedTownhouse]: [
    RepliersListingStyle.StackedTownhouse,
  ],
  [StyleFilterOptionLabel.Cottage]: [
    RepliersListingStyle.Cottage,
    RepliersListingStyle.Chalet,
    RepliersListingStyle.Log,
    RepliersListingStyle.AFrame,
  ],
  [StyleFilterOptionLabel.Other]: [
    RepliersListingStyle.Other,
    RepliersListingStyle.MultiLevel,
    RepliersListingStyle.BachelorStudio,
    RepliersListingStyle.Apartment,
    RepliersListingStyle.OneStoreyOrApt,
    RepliersListingStyle.Mobile,
    RepliersListingStyle.Link,
    RepliersListingStyle.Contemporary,
    RepliersListingStyle.GardenHouse,
  ],
};

export enum PropertyTypeFilterOptionLabels {
  Detached = 'Detached',
  SemiDetached = 'Semi-Detached',
  CondoApartment = 'Condo Apartment',
  CondoTownhouse = 'Condo Townhouse',
  FreeholdTownhouse = 'Freehold Townhouse',
  Multiplex = 'Multiplex',
  Land = 'Land',
  Link = 'Link',
  Other = 'Other',
}

export const propertyTypeFilterOptions: Array<{
  label: PropertyTypeFilterOptionLabels;
  repliersPropertyTypesIncluded: Array<RepliersListingPropertyType>;
}> = [
  {
    label: PropertyTypeFilterOptionLabels.Detached,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.Detached,
      RepliersListingPropertyType.RuralResidential,
      RepliersListingPropertyType.Cottage,
      RepliersListingPropertyType.DetachedCondo,
      RepliersListingPropertyType.SingleFamilyResidence,
      RepliersListingPropertyType.SingleFamily,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.SemiDetached,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.SemiDetached,
      RepliersListingPropertyType.SemiDetachedCondo,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.CondoApartment,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.CondoApartment,
      RepliersListingPropertyType.CoopApt,
      RepliersListingPropertyType.CoOwnershipApt,
      RepliersListingPropertyType.LeaseholdCondo,
      RepliersListingPropertyType.CommercialElementCondo,
      RepliersListingPropertyType.Apartment,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.CondoTownhouse,
    repliersPropertyTypesIncluded: [RepliersListingPropertyType.CondoTownhouse],
  },
  {
    label: PropertyTypeFilterOptionLabels.FreeholdTownhouse,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.FreeholdTownhouse,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.Multiplex,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.Multiplex,
      RepliersListingPropertyType.StoreWithAptOffice,
      RepliersListingPropertyType.Duplex,
      RepliersListingPropertyType.Triplex,
      RepliersListingPropertyType.StoreWithAptOffc,
      RepliersListingPropertyType.Fourplex,
      RepliersListingPropertyType.LowerLevel,
      RepliersListingPropertyType.UpperLevel,
      RepliersListingPropertyType.DuplexUpDown,
      RepliersListingPropertyType.DuplexSideSide,
      RepliersListingPropertyType.Multi3Unit,
      RepliersListingPropertyType.Multi4Unit,
      RepliersListingPropertyType.Multi5Unit,
      RepliersListingPropertyType.Multi6To9Unit,
      RepliersListingPropertyType.MultiFamily,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.Land,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.VacantLand,
      RepliersListingPropertyType.Farm,
      RepliersListingPropertyType.Land,
      RepliersListingPropertyType.VacantLandCondo,
      RepliersListingPropertyType.Agriculture,
    ],
  },
  {
    label: PropertyTypeFilterOptionLabels.Link,
    repliersPropertyTypesIncluded: [RepliersListingPropertyType.Link],
  },
  {
    label: PropertyTypeFilterOptionLabels.Other,
    repliersPropertyTypesIncluded: [
      RepliersListingPropertyType.LowerLevel,
      RepliersListingPropertyType.Other,
      RepliersListingPropertyType.ParkingSpace,
      RepliersListingPropertyType.UpperLevel,
      RepliersListingPropertyType.MobileTrailer,
      RepliersListingPropertyType.Locker,
      RepliersListingPropertyType.Room,
      RepliersListingPropertyType.TimeShare,
      RepliersListingPropertyType.Investment,
      RepliersListingPropertyType.MobileHome,
      RepliersListingPropertyType.ModularHome,
      RepliersListingPropertyType.MixedUse,
      RepliersListingPropertyType.Mixed,
      RepliersListingPropertyType.RoomingHouse,
      RepliersListingPropertyType.DeededParking,
    ],
  },
];

export const SearchFiltersSchema = z.object({
  showSoldOrLeased: z.boolean().optional().catch(undefined),
  showActive: z.boolean().optional().catch(undefined),
  transactionType: z
    .nativeEnum(RepliersListingTransactionType)
    .optional()
    .catch(undefined),
  minPrice: z.coerce.number().optional().catch(undefined),
  maxPrice: z.coerce.number().nullable().optional().catch(undefined),
  numBedrooms: z
    .object(
      BEDROOM_KEYS.reduce((acc, key) => {
        acc[key] = z.boolean();
        return acc;
      }, {} as Record<BedroomsKeys, z.ZodBoolean>),
    )
    .optional()
    .catch(undefined),
  minBedrooms: z.number().optional().catch(undefined),
  maxBedrooms: z.number().nullable().optional().catch(undefined),
  numBathrooms: z
    .object(
      BATHROOMS_KEYS.reduce((acc, key) => {
        acc[key] = z.boolean();
        return acc;
      }, {} as Record<BathroomsKeys, z.ZodBoolean>),
    )
    .optional()
    .catch(undefined),
  minBathrooms: z.number().optional().catch(undefined),
  maxBathrooms: z.number().nullable().optional().catch(undefined),
  hasDen: z.boolean().nullable().optional().catch(undefined),
  minLotFront: z.number().optional().catch(undefined),
  maxLotFront: z.number().nullable().optional().catch(undefined),
  lotMeasurementUnits: z.nativeEnum(Units).optional().catch(undefined),
  propertyTypes: z
    .array(z.nativeEnum(PropertyTypeFilterOptionLabels))
    .optional()
    .catch(undefined),
  styles: z
    .array(z.nativeEnum(StyleFilterOptionLabel))
    .optional()
    .catch(undefined),
  balconies: z
    .array(z.nativeEnum(BalconyFilterOptionLabel))
    .optional()
    .catch(undefined),
  condoExposures: z
    .array(z.nativeEnum(CondoExposureFilterOptionLabel))
    .optional()
    .catch(undefined),
  maxListedDaysAgo: z.number().nullable().optional().catch(undefined),
  minListDate: z.string().nullable().optional().catch(undefined),
  maxListDate: z.string().nullable().optional().catch(undefined),
  maxSoldDaysAgo: z.number().nullable().optional().catch(undefined),
  minSoldDate: z.string().nullable().optional().catch(undefined),
  maxSoldDate: z.string().nullable().optional().catch(undefined),
  onlyShowPriceChangedStatus: z.boolean().optional().catch(undefined),
  minImages: z.number().optional().catch(undefined),
  hasSupportedVirtualTourUrl: z
    .boolean()
    .nullable()
    .optional()
    .catch(undefined),
  neighborhoodName: z.string().nullable().optional().catch(undefined),
  cities: z.array(z.string()).optional().catch(undefined),
  areas: z.array(z.string()).optional().catch(undefined),
  districts: z.array(z.string()).optional().catch(undefined),
  hasPool: z.boolean().nullable().optional().catch(undefined),
  hasBackyard: z.boolean().nullable().optional().catch(undefined),
  hasBasement: z.boolean().nullable().optional().catch(undefined),
  hasFinishedBasement: z.boolean().nullable().optional().catch(undefined),
  hasSeparateEntranceBasement: z
    .boolean()
    .nullable()
    .optional()
    .catch(undefined),
  hasBasementApartment: z.boolean().nullable().optional().catch(undefined),
  isOnWaterfront: z.boolean().nullable().optional().catch(undefined),
  isFixerUpper: z.boolean().nullable().optional().catch(undefined),
  hasOfferDate: z.boolean().nullable().optional().catch(undefined),
  keywords: z.array(z.string()).optional().catch(undefined),
  minMaintenanceFee: z.number().optional().catch(undefined),
  maxMaintenanceFee: z.number().nullable().optional().catch(undefined),
  lastStatuses: z
    .array(z.nativeEnum(RepliersListingLastStatus))
    .optional()
    .catch(undefined),
  minGarageSpaces: z.number().optional().catch(undefined),
  maxGarageSpaces: z.number().nullable().optional().catch(undefined),
  minParkingSpaces: z.number().optional().catch(undefined),
  maxParkingSpaces: z.number().nullable().optional().catch(undefined),
  geographicAreas: z.array(geographicAreaSchema).optional().catch(undefined),
  buildingClasses: z
    .array(z.nativeEnum(RepliersListingBuildingClass))
    .optional()
    .catch(undefined),
  minOfferCount: z.number().nullable().optional().catch(undefined),
  maxOfferCount: z.number().nullable().optional().catch(undefined),
  minOfferDate: z.string().nullable().optional().catch(undefined), // ISO format datetime string
  maxOfferDate: z.string().nullable().optional().catch(undefined), // ISO format datetime string

  // @deprecated Jun 1 2023
  listDate: z.nativeEnum(ListDateFilterLabels).optional().catch(undefined),
  soldDate: z.nativeEnum(SoldDateFilterLabels).optional().catch(undefined),
  maxDistanceToSubwayLinesMetres: z
    .number()
    .nullable()
    .optional()
    .catch(undefined),
  minBuildingAgeYears: z.number().nullable().optional().catch(undefined),
  maxBuildingAgeYears: z.number().nullable().optional().catch(undefined),
  isForSaleByOwner: z.boolean().nullable().optional().catch(undefined),
  isPowerOfSale: z.boolean().nullable().optional().catch(undefined),
  minSquareFeet: z.number().nullable().optional().catch(undefined),
  maxSquareFeet: z.number().nullable().optional().catch(undefined),
});

export const nonGeoSearchFiltersSchema = SearchFiltersSchema.omit({
  geographicAreas: true,
  districts: true,
  areas: true,
  cities: true,
  neighborhoodName: true,
});

export type SearchFilters = z.infer<typeof SearchFiltersSchema>;

export type SearchFilterViewLevel = 'none' | 'basic' | 'advanced' | 'all';
